import { ALGOS_E } from "@constants/Algos";
import {
  AddMinerProfile_RequestTypes,
  EditMinerProfile_RequestTypes,
  IMinerProfileEditParams,
  IMinerProfileSaveParams,
} from "@mobx/api/minerProfiles";
import apiService, {
  createStore,
  RequestControl,
  withLoading,
} from "@mobx/services/http";
import { IMinerProfileBase, IMinerProfileMeta } from "@services/minerProfiles";
import {
  calculateHash,
  getDevivedHash,
  HashFactorName,
} from "@utils/hash.utils";
import { action, computed, makeObservable, observable, runInAction } from "mobx";

export type MinerProfileAlgo = Exclude<ALGOS_E, ALGOS_E.ALL>;

export interface IMinerProfileEditorDraft {
  name: string;
  hash: MinerProfileAlgo;
  performance: number;
  performanceFactor: HashFactorName;
  performanceInput: string;
  consumption: number;
  consumptionInput: string;
  err: number;
  width: string;
  height: string;
  depth: string;
}

export interface IMinerProfileEditorErrors {
  name?: boolean;
  performance?: boolean;
  consumption?: boolean;
  width?: boolean;
  height?: boolean;
  depth?: boolean;
}

export interface IMinerProfileDisplayValue {
  readonly value: number;
  readonly unit: string;
}

export interface IMinerProfileEditorStore {
  isLoading: boolean;
  profileId?: number;
  original?: IMinerProfileBase;
  draft?: IMinerProfileEditorDraft;
  errors: IMinerProfileEditorErrors;
  loadProfile(profile: IMinerProfileBase): void;
  createEmpty(): void;
  clear(): void;
  resetDraft(): void;
  setName(value: string): void;
  setHash(value: MinerProfileAlgo): void;
  setPerformance(value: number): void;
  setPerformanceFactor(value: HashFactorName): void;
  setEnteredPerformance(value: number): void;
  setPerformanceInput(value: string): void;
  setConsumption(value: number): void;
  setConsumptionInput(value: string): void;
  setErr(value: number): void;
  setDimensions(
    data: Partial<Pick<IMinerProfileEditorDraft, "width" | "height" | "depth">>
  ): void;
  validate(): boolean;
  save(): Promise<boolean>;
  readonly effectiveConsumption: number;
  readonly enteredPerformanceValue: number;
  readonly displayPerformance: IMinerProfileDisplayValue;
  readonly efficiency: number;
  readonly efficiencyUnit: string;
  readonly canSave: boolean;
  readonly savePayload?: IMinerProfileSaveParams | IMinerProfileEditParams;
}

const EMPTY_ERRORS: IMinerProfileEditorErrors = {};
const DEFAULT_HASH: MinerProfileAlgo = ALGOS_E.SHA256;

const normalizeHash = (hash?: ALGOS_E): MinerProfileAlgo =>
  hash && hash !== ALGOS_E.ALL ? hash : DEFAULT_HASH;

const normalizeMeta = (
  meta?: IMinerProfileMeta | string
): Required<IMinerProfileMeta> => {
  const parsedMeta = (() => {
    if (typeof meta !== "string") return meta;

    try {
      return JSON.parse(meta) as IMinerProfileMeta;
    } catch {
      return undefined;
    }
  })();

  return {
    width: parsedMeta?.width ?? 0,
    height: parsedMeta?.height ?? 0,
    depth: parsedMeta?.depth ?? 0,
    err: parsedMeta?.err ?? 0,
    efficiency: parsedMeta?.efficiency ?? 0,
  };
};

const getPerformanceFactor = (
  performance: number,
  hash: MinerProfileAlgo
): HashFactorName => {
  const [, unit = ""] = getDevivedHash(performance, { unit: hash }).split(" ");
  const factor = unit.replace(/(H|Sol)\/s$/, "");
  return (factor as HashFactorName) || HashFactorName.N;
};

const createDraft = (profile?: IMinerProfileBase): IMinerProfileEditorDraft => {
  const meta = normalizeMeta(profile?.meta);
  const performance = Number(profile?.performance) || 0;
  const hash = normalizeHash(profile?.hash);
  return {
    name: profile?.name || "",
    hash,
    performance,
    performanceFactor: getPerformanceFactor(performance, hash),
    performanceInput: performance > 0 ? `${Number(getDevivedHash(performance, {
      unit: hash,
      hashFactor: getPerformanceFactor(performance, hash),
    }).split(" ")[0])}` : "",
    consumption: Number(profile?.consumption) || 0,
    consumptionInput:
      Number(profile?.consumption) > 0 ? `${Number(profile?.consumption)}` : "",
    err: Number(meta.err) || 0,
    width: `${meta.width || ""}`,
    height: `${meta.height || ""}`,
    depth: `${meta.depth || ""}`,
  };
};

export const createMinerProfileEditorStore_ = () => (rc: RequestControl) => {
  const addProfile = apiService.createRequest(AddMinerProfile_RequestTypes);
  const editProfile = apiService.createRequest(EditMinerProfile_RequestTypes);

  const self: IMinerProfileEditorStore = {
    isLoading: false,
    profileId: undefined,
    original: undefined,
    draft: undefined,
    errors: EMPTY_ERRORS,

    loadProfile(profile) {
      self.profileId = profile.id;
      self.original = profile;
      self.draft = createDraft(profile);
      self.errors = EMPTY_ERRORS;
    },

    createEmpty() {
      self.profileId = undefined;
      self.original = undefined;
      self.draft = createDraft();
      self.errors = EMPTY_ERRORS;
    },

    clear() {
      self.profileId = undefined;
      self.original = undefined;
      self.draft = undefined;
      self.errors = EMPTY_ERRORS;
    },

    resetDraft() {
      if (self.original) {
        self.draft = createDraft(self.original);
      } else {
        self.draft = createDraft();
      }
      self.errors = EMPTY_ERRORS;
    },

    setName(value) {
      if (!self.draft) return;
      self.draft.name = value;
    },

    setHash(value) {
      if (!self.draft) return;
      self.draft = {
        ...self.draft,
        hash: value,
      };
      //dbg.log("MINER_PROFILE_EDITOR setHash:", { hash: self.draft.hash });
    },

    setPerformance(value) {
      if (!self.draft) return;
      self.draft.performance = value;
      self.draft.performanceInput = value > 0 ? `${self.enteredPerformanceValue}` : "";
    },

    setPerformanceFactor(value) {
      if (!self.draft) return;
      self.draft.performanceFactor = value;
      if (self.draft.performanceInput.trim() === "") {
        self.draft.performance = 0;
        return;
      }
      const normalized = Number(self.draft.performanceInput);
      if (Number.isFinite(normalized)) {
        self.draft.performance = calculateHash(normalized, value);
      }
    },

    setEnteredPerformance(value) {
      if (!self.draft) return;
      self.draft.performance = calculateHash(value, self.draft.performanceFactor);
      self.draft.performanceInput = value > 0 ? `${value}` : "";
    },

    setPerformanceInput(value) {
      if (!self.draft) return;
      self.draft.performanceInput = value;
      if (value.trim() === "") {
        self.draft.performance = 0;
        //dbg.log("MINER_PROFILE_EDITOR setPerformanceInput:", { value, performanceInput: self.draft.performanceInput, performance: self.draft.performance, factor: self.draft.performanceFactor });
        return;
      }
      const normalized = Number(value);
      if (Number.isFinite(normalized)) {
        self.draft.performance = calculateHash(
          normalized,
          self.draft.performanceFactor
        );
      }
      //dbg.log("MINER_PROFILE_EDITOR setPerformanceInput:", { value, performanceInput: self.draft.performanceInput, performance: self.draft.performance, factor: self.draft.performanceFactor });
    },

    setConsumption(value) {
      if (!self.draft) return;
      self.draft.consumption = value;
      self.draft.consumptionInput = value > 0 ? `${value}` : "";
    },

    setConsumptionInput(value) {
      if (!self.draft) return;
      self.draft.consumptionInput = value;
      if (value.trim() === "") {
        self.draft.consumption = 0;
        //dbg.log("MINER_PROFILE_EDITOR setConsumptionInput:", { value, consumptionInput: self.draft.consumptionInput, consumption: self.draft.consumption });
        return;
      }
      const normalized = Number(value);
      if (Number.isFinite(normalized)) {
        self.draft.consumption = normalized;
      }
      //dbg.log("MINER_PROFILE_EDITOR setConsumptionInput:", { value, consumptionInput: self.draft.consumptionInput, consumption: self.draft.consumption });
    },

    setErr(value) {
      if (!self.draft) return;
      self.draft.err = value;
    },

    setDimensions(data) {
      if (!self.draft) return;
      self.draft = { ...self.draft, ...data };
    },

    validate() {
      if (!self.draft) return false;

      const errors: IMinerProfileEditorErrors = {};
      if (!self.draft.name.trim()) errors.name = true;
      if (!(self.draft.performance > 0)) errors.performance = true;
      if (!(self.draft.consumption > 0)) errors.consumption = true;
      if (!self.draft.width.trim()) errors.width = true;
      if (!self.draft.height.trim()) errors.height = true;
      if (!self.draft.depth.trim()) errors.depth = true;

      self.errors = errors;
      return Object.keys(errors).length === 0;
    },

    get effectiveConsumption() {
      if (!self.draft) return 0;
      const consumption = Number(self.draft.consumption) || 0;
      const err = Number(self.draft.err) || 0;
      return Number((consumption + (consumption * err) / 100).toFixed(2));
    },

    get enteredPerformanceValue() {
      if (!self.draft) return 0;
      const [value] = getDevivedHash(self.draft.performance, {
        unit: self.draft.hash,
        hashFactor: self.draft.performanceFactor,
      }).split(" ");
      return Number(value) || 0;
    },

    get displayPerformance() {
      if (!self.draft) return { value: 0, unit: "H/s" };
      const [value, unit] = getDevivedHash(self.draft.performance, {
        unit: self.draft.hash,
      }).split(" ");
      return {
        value: Number(value) || 0,
        unit: unit || "H/s",
      };
    },

    get efficiency() {
      const perf = self.displayPerformance.value;
      const consumption = self.effectiveConsumption;
      if (!(perf > 0) || !(consumption > 0)) return 0;
      return Number((perf / consumption).toFixed(2));
    },

    get efficiencyUnit() {
      const perfUnit = self.displayPerformance.unit.split("/")[0] || "H";
      return `${perfUnit}/кВтч`;
    },

    get canSave() {
      if (!self.draft) return false;

      return (
        !!self.draft.name.trim() &&
        self.draft.performance > 0 &&
        self.draft.consumption > 0 &&
        !!self.draft.width.trim() &&
        !!self.draft.height.trim() &&
        !!self.draft.depth.trim()
      );
    },

    get savePayload() {
      if (!self.draft) return;

      const payload: IMinerProfileSaveParams = {
        name: self.draft.name.trim(),
        hash: self.draft.hash,
        performance: self.draft.performance,
        consumption: self.draft.consumption,
        meta: JSON.stringify({
          width: self.draft.width,
          height: self.draft.height,
          depth: self.draft.depth,
          err: self.draft.err,
          efficiency: self.efficiency,
        }),
      };

      if (!self.profileId) return payload;

      return {
        ...payload,
        id: self.profileId,
        edit: true,
      };
    },

    async save() {
      if (!self.validate()) return false;

      const payload = self.savePayload;
      if (!payload) return false;

      const response = "edit" in payload
        ? await withLoading(self, editProfile(rc.createRequestHandle(), payload))
        : await withLoading(self, addProfile(rc.createRequestHandle(), payload));

      if (!response) return false;

      runInAction(() => {
        if (!self.draft) return;
        if (!self.profileId) {
          self.profileId = response.Record;
        }
        self.original = {
          id: self.profileId as number,
          name: self.draft.name.trim(),
          hash: self.draft.hash,
          performance: self.draft.performance,
          consumption: self.draft.consumption,
          meta: JSON.parse((self.savePayload as IMinerProfileSaveParams).meta),
        };
        self.errors = EMPTY_ERRORS;
      });

      return true;
    },
  };

  return makeObservable(self, {
    isLoading: observable,
    profileId: observable,
    original: observable,
    draft: observable,
    errors: observable,
    effectiveConsumption: computed,
    enteredPerformanceValue: computed,
    displayPerformance: computed,
    efficiency: computed,
    efficiencyUnit: computed,
    canSave: computed,
    savePayload: computed,
    loadProfile: action,
    createEmpty: action,
    clear: action,
    resetDraft: action,
    setName: action,
    setHash: action,
    setPerformance: action,
    setPerformanceFactor: action,
    setEnteredPerformance: action,
    setPerformanceInput: action,
    setConsumption: action,
    setConsumptionInput: action,
    setErr: action,
    setDimensions: action,
    validate: action,
    save: action,
  });
};

export const createMinerProfileEditorStore = () =>
  createStore(createMinerProfileEditorStore_());

const minerProfileEditorStore = createMinerProfileEditorStore();

export default minerProfileEditorStore;
